import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ConnectionApikey } from '~/components/settings/components/types'
import { ConnectionType, Connections } from '~/constants/connections'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  useConnectionMetadata,
  useConnectionStatus,
  useDeleteConnection
} from '~/hooks/organization'
import { getConnectionMetadataList } from '~/services/organizationService'
import { QueryKeys } from '~/services/queryClient'

interface ConnectionManagementValue {
  connList: string[]
  connectedConnList: string[]
  /** True while the connection-status query is loading (no data yet). Consumers
   *  must wait for this before treating an empty connectedConnList as "not
   *  connected" (otherwise a fresh page load looks disconnected mid-fetch). */
  isConnectionStatusLoading: boolean
  repositoryConnectionStates: Record<string, boolean>
  apiKeyValues: Record<string, ConnectionApikey>
  isUpdatingToken: Record<string, boolean>
  getMetaData: () => Promise<Record<string, ConnectionApikey>>
  handleDeleteConnection: (connType: string) => Promise<{ success: boolean; error?: string }>
  updateApiKeyValue: (connId: string, value: ConnectionApikey) => void
  toggleRepositoryConnectionState: (connId: string) => void
  closeRepositoryConnectionState: (connId: string) => void
  refreshConnections: () => void
}

/**
 * Connection management state for the repositories settings form.
 *
 * Was previously a Provider mounted in `WorkspaceLevelProvider`, but the only
 * production consumer is `RepositoriesSettingsTab` — the global Provider was carrying
 * settings-modal-scoped state through the entire authenticated tree. Now it's
 * a regular hook called from `RepositoriesSettingsTab`; instance-per-mount is fine
 * because the form is a singleton view.
 */
export function useConnectionManagementContext(): ConnectionManagementValue {
  const { currentWorkspace, isLoading: isWorkspaceLoading } = useWorkspaceContext()
  const { data: connectionMetadata } = useConnectionMetadata()
  const { data: connectionStatus, isLoading: isConnectionStatusLoading } = useConnectionStatus()
  const { mutateAsync: deleteConnectionAsync } = useDeleteConnection()
  const queryClient = useQueryClient()

  const defaultConnList = useMemo(
    () => [
      ConnectionType.GitHub,
      ConnectionType.BitBucket,
      ConnectionType.GitLab,
      ConnectionType.AzureDevOps
    ],
    []
  )

  const [repositoryConnectionStates, setRepositoryConnectionStates] = useState<
    Record<string, boolean>
  >({})
  const [apiKeyValues, setApiKeyValues] = useState<Record<string, ConnectionApikey>>({})
  const [isUpdatingToken] = useState<Record<string, boolean>>({})
  const [connList, setConnList] = useState<string[]>(defaultConnList)
  const [connectedConnList, setConnectedConnList] = useState<string[]>([])

  // Update connection lists when connection status changes
  useEffect(() => {
    if (connectionStatus) {
      setConnList([...defaultConnList, ...(connectionStatus.connections || [])])
      setConnectedConnList([...(connectionStatus.connected_connections || [])])
    } else {
      // Reset to defaults if no connection status available
      setConnList(defaultConnList)
      setConnectedConnList([])
    }
  }, [connectionStatus, defaultConnList])

  const getMetaData = useCallback(async () => {
    if (isWorkspaceLoading) return {}
    if (!currentWorkspace) {
      throw new Error('Workspace not found')
    }
    const data = connectionMetadata || (await getConnectionMetadataList(currentWorkspace.uuid))
    const tokens = Object.values(Connections).reduce(
      (acc, conn) => ({
        ...acc,
        [conn.id]: data[conn.id]
          ? data[conn.id]
          : {
              token: '',
              baseUrl: ''
            }
      }),
      {} as Record<string, ConnectionApikey>
    )

    setApiKeyValues(tokens)
    return tokens
  }, [connectionMetadata, currentWorkspace, isWorkspaceLoading])

  // Update apiKeyValues when connectionMetadata changes
  useEffect(() => {
    getMetaData()
  }, [getMetaData])

  const handleDeleteConnection = useCallback(
    async (connType: string) => {
      try {
        await deleteConnectionAsync(connType)
        return { success: true }
      } catch (e) {
        // Surface the backend reason so the caller can show an accurate toast
        // (e.g. expired AWS session vs. the agent being unreachable).
        return {
          success: false,
          error: e instanceof Error ? e.message : String(e)
        }
      }
    },
    [deleteConnectionAsync]
  )

  const updateApiKeyValue = useCallback((connId: string, value: ConnectionApikey) => {
    setApiKeyValues(prev => ({
      ...prev,
      [connId]: value
    }))
  }, [])

  const toggleRepositoryConnectionState = useCallback((connId: string) => {
    setRepositoryConnectionStates(prev => ({
      ...prev,
      [connId]: !prev[connId]
    }))
  }, [])

  const closeRepositoryConnectionState = useCallback((connId: string) => {
    setRepositoryConnectionStates(prev => ({
      ...prev,
      [connId]: false
    }))
  }, [])

  // Refresh connections after OAuth success or other connection changes
  const refreshConnections = useCallback(() => {
    console.log('Refreshing connection data...')
    // Invalidate connection queries to trigger refetch
    queryClient.invalidateQueries({ queryKey: [QueryKeys.ConnectionStatus] })
    queryClient.invalidateQueries({ queryKey: [QueryKeys.ConnectionMetadata] })
  }, [queryClient])

  return useMemo(
    () => ({
      connList,
      connectedConnList,
      isConnectionStatusLoading,
      repositoryConnectionStates,
      apiKeyValues,
      isUpdatingToken,
      getMetaData,
      handleDeleteConnection,
      updateApiKeyValue,
      toggleRepositoryConnectionState,
      closeRepositoryConnectionState,
      refreshConnections
    }),
    [
      connList,
      connectedConnList,
      isConnectionStatusLoading,
      repositoryConnectionStates,
      apiKeyValues,
      isUpdatingToken,
      getMetaData,
      handleDeleteConnection,
      updateApiKeyValue,
      toggleRepositoryConnectionState,
      closeRepositoryConnectionState,
      refreshConnections
    ]
  )
}
